import clsx from 'clsx'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Fieldset from '@/components-latest/layout/Fieldset'
import Button from '@/components-latest/primitives/Button'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components-latest/primitives/Controls/Select'
import { ExclamationPoint } from '@/components-latest/primitives/Icons'
import Input from '@/components-latest/primitives/Input'
import ScrollArea from '@/components-latest/primitives/Scrollable'
import Text from '@/components-latest/primitives/Text'
import { Retry } from '@/components-latest/primitives/Icons'
import Styles from './ChooseCartDiscountDetails.module.scss'
import { useFetchDiscountAmountSuggestionsQuery } from '@/ducks/offers/api/offers.api'
import CommonModal from '@/components-latest/shared/Modals/CommonModal'

export enum DiscountType {
  Percentage = 'Percentage',
  FlatRate = 'Flat'
}

type Props = {
  discountType?: DiscountType
  onDiscountTypeChange?: (type: DiscountType) => void
  discountValue: string
  onDiscountValueChange: (value: string) => void
  minimumAmount: string
  onMinimumAmountChange: (value: string) => void
  maximumOff?: string
  onMaximumOffChange?: (value: string) => void
  expirationValue: string
  onExpirationChange: (value: string) => void
  className?: string
}

const ChooseCartDiscountDetails: React.FC<Props> = ({
  className,
  discountType = DiscountType.Percentage,
  onDiscountTypeChange = () => {},
  discountValue,
  onDiscountValueChange,
  minimumAmount,
  onMinimumAmountChange,
  maximumOff,
  onMaximumOffChange,
  expirationValue,
  onExpirationChange
}) => {
  const { t } = useTranslation()

  const percentsValues = useMemo(() => [5, 10, 15, 20, 30], [])
  const expirationValues = useMemo(
    () => ['3h', '12h', '24h', '3d', '7d', '15d', '30d'],
    []
  )
  const [isFlatRateSelected, setIsFlatRateSelected] = useState(discountType === DiscountType.FlatRate)
  const { data: flatRateValues = { suggested_values: [], currency_symbol: 'DZD' }, error: flatRateValuesError, refetch } = useFetchDiscountAmountSuggestionsQuery(undefined, { skip: !isFlatRateSelected })
  const currency = flatRateValues?.currency_symbol || 'DZD'

  const [isInvalidMinimumAmount, setIsInvalidMinimumAmount] = useState(false)
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false)

  useEffect(() => {
    if (isFlatRateSelected && Array.isArray(flatRateValues.suggested_values) && flatRateValues.suggested_values.length > 0) {
      onDiscountValueChange(String(flatRateValues.suggested_values[0]))
    }
  }, [isFlatRateSelected, flatRateValues, onDiscountValueChange])

  useEffect(() => {
    if (flatRateValuesError) {
      setIsErrorModalVisible(true)
    }
  }, [flatRateValuesError])

  const handleMinimumAmountChange = (value: string) => {
    onMinimumAmountChange(value)

    const minAmount = parseFloat(value)
    const discount = parseFloat(discountValue)

    if (minAmount < discount) {
      setIsInvalidMinimumAmount(true)
    } else {
      setIsInvalidMinimumAmount(false)
    }
  }

  const handleRetry = () => {
    refetch()
    setIsErrorModalVisible(false)
  }

  const handleDiscountTypeChange = (type: DiscountType) => {
    onDiscountTypeChange(type)
    setIsFlatRateSelected(type === DiscountType.FlatRate)
    if (type === DiscountType.Percentage) {
      onDiscountValueChange(String(percentsValues[0]))
    }
  }

  return (
    <ContentBlock className={clsx(className, Styles.contentBlock)}>
      <div className={Styles.inReview}>
        <ExclamationPoint />
        <Text variant="body" weight="semiBold">
          {t('offers.select-offer-type.cart-discount-backdrop.description')}
        </Text>
      </div>

      {/* Section 1: Discount Type */}
      <div className={Styles.section}>
        <Text variant="title-3">
          {t('offers.create-cart-discount.discount-type.title')}
        </Text>
        <div className={Styles.buttonGroup}>
          <Button
            variant={discountType === DiscountType.Percentage ? 'branded' : 'secondary'}
            onClick={() => handleDiscountTypeChange(DiscountType.Percentage)}
            className={Styles.discountButton}>
            {t('offers.create-cart-discount.discount-type.percentage')}
          </Button>
          <Button
            variant={discountType === DiscountType.FlatRate ? 'branded' : 'secondary'}
            onClick={() => handleDiscountTypeChange(DiscountType.FlatRate)}
            className={Styles.discountButton}>
            {t('offers.create-cart-discount.discount-type.flat')}
          </Button>
        </div>
      </div>

      <div className={Styles.sectiondetails}>
        <hr />
        <Text variant="title-3">
          {t('offers.create-cart-discount.discount-type.details')}
        </Text>

        {/* Discount Amount */}
        <div className={Styles.selectWrapper}>
          <Text variant="heading-05">
            {t('offers.create-cart-discount.discount-type.amount.title')}
          </Text>
          <Select onValueChange={onDiscountValueChange} value={discountValue}>
            <SelectTrigger className={clsx(Styles.selectTrigger, className)}>
              <SelectValue asChild>
                <Text>
                  {discountValue}
                  {discountType === DiscountType.Percentage ? '%' : ` ${currency}`}
                </Text>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <ScrollArea viewportClassName={Styles.scrollArea}>
                {(discountType === DiscountType.Percentage
                  ? percentsValues
                  : Array.isArray(flatRateValues.suggested_values) ? flatRateValues.suggested_values : []
                ).map((value: number) => (
                  <SelectItem key={value} value={String(value)}>
                    {value}
                    {discountType === DiscountType.Percentage ? '%' : ` ${currency}`}
                  </SelectItem>
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>
        </div>

        {/* Minimum Amount */}
        <div className={Styles.selectWrapper}>
          <Text variant="heading-05">
            {t('offers.create-cart-discount.target-amount.title')}
          </Text>
          <Fieldset variant="nextBody" weight="semiBold" label="">
            <Input
              type="number"
              value={minimumAmount}
              onChange={e => handleMinimumAmountChange(e.target.value)}
              placeholder={t(
                'offers.create-cart-discount.target-amount.placeholder'
              )}
              prefix={` ${currency}`}
              intent={isInvalidMinimumAmount ? 'negative' : 'default'}
              className={Styles.input}
              onFocus={() => setIsInvalidMinimumAmount(false)}
            />

            {isInvalidMinimumAmount && (
              <Text variant="caption" className={Styles.errorText}>
                {t('offers.select-offer-type.minimum-amount-validation')}
              </Text>
            )}
          </Fieldset>
        </div>

        {/* Maximum Off (Only for Percentage Discounts) */}
        {discountType === DiscountType.Percentage && (
          <div className={Styles.selectWrapper}>
            <Text variant="heading-05">
              {t('offers.create-cart-discount.max-off.title')}
            </Text>
            <Input
              type="number"
              value={maximumOff || ''}
              onChange={e => onMaximumOffChange?.(e.target.value)}
              placeholder={t('offers.create-cart-discount.max-off.placeholder')}
              prefix={` ${currency}`}
            />
          </div>
        )}

        {/* Expiration Date */}
        <div className={Styles.selectWrapper}>
          <Text variant="heading-05">
            {t('offers.create-cart-discount.expiration-date.title')}
          </Text>
          <Select
            onValueChange={onExpirationChange}
            value={expirationValue}
            aria-label={t('offers.create-cart-discount.expiration-date.title')}>
            <SelectTrigger className={clsx(Styles.selectTrigger, className)}>
              <SelectValue asChild>
                <Text>{t(`offers.offers-details.${expirationValue}`)}</Text>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <ScrollArea viewportClassName={Styles.scrollArea}>
                {expirationValues.map(value => (
                  <SelectItem key={value} value={value}>
                    {t(`offers.offers-details.${value}`)}
                  </SelectItem>
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Error Modal */}
      {flatRateValuesError && (
        <CommonModal
          open={isErrorModalVisible}
          title={t('offers.cart-discount-flat-amount-error.title')}
          onOpenChange={() => setIsErrorModalVisible(false)}
          body={
            <div className={Styles.modalImg}>
              <img 
                src="/images/offers/api-failed.svg"
                alt="coin with percent symbol"
              />
              <Text variant="heading-04" align="center">{t('offers.cart-discount-flat-amount-error.title')}</Text>
              <Text variant="subtitle" align="center">
              {t('offers.cart-discount-flat-amount-error.description')}
              </Text>
            </div>
          }
          footer={
            <div className={Styles.footerCenter}>
              <Button
                onClick={handleRetry}
                shape="rounded"
                variant="branded"
                icon={<Retry />}>
                Try again
              </Button>
            </div>
          }
          className={clsx(Styles.modalWidth)}
        />
      )}
    </ContentBlock>
  )
}

export default ChooseCartDiscountDetails
